import { INSTANCE_PATH_PREFIX } from "@shared/instanceAddress";
import { isGlobalPath } from "@/lib/routes";

/**
 * The instance in the address (round 9 T-58, owner 2026-10-06, reversing FR-22 with safeguards).
 *
 * Every instance-scoped screen lives under `/i/<instance id>/...`: `/i/<id>/runs/<run id>` is a
 * run of that instance, so a link names its instance, a reload keeps it and each tab follows its
 * own address. The platform and personal screens, the picker and the sign-in routes stay outside
 * it (`isGlobalPath`). The id in the address only CHOOSES; it is never trusted for data: the
 * client selects it only when `GET /me` lists it, and the server re-checks the `x-instance-id`
 * header on every request (`server/middleware/instanceContext.ts`), so a forbidden id shows the
 * in-shell "no access" state and reads nothing.
 *
 * A screen's own paths stay relative (`/connections`, `ROUTES`): `App.tsx` mounts the shell in an
 * `InstanceRouter`, which adds the prefix to every link and navigation and takes it off what a
 * screen reads from `useLocation`.
 */

const INSTANCE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a value has the shape of an instance id (a uuid). */
export function isInstanceIdShape(value: string): boolean {
  return INSTANCE_ID_PATTERN.test(value);
}

/**
 * The instance segment of an address and the screen's path inside it. `instanceId` is the raw
 * segment (lower-cased when it is a uuid, else as written, so a malformed one can be refused), or
 * null when the address is not under `/i/`. `inner` is the rest, `/` when there is none.
 */
export function splitInstancePath(path: string): {
  instanceId: string | null;
  inner: string;
  /** The segment exactly as the address wrote it (its case included), or null. */
  segment: string | null;
} {
  const prefix = `${INSTANCE_PATH_PREFIX}/`;
  if (!path.startsWith(prefix)) return { instanceId: null, inner: path, segment: null };
  const rest = path.slice(prefix.length);
  const cut = rest.search(/[/?#]/);
  const segment = cut === -1 ? rest : rest.slice(0, cut);
  const tail = cut === -1 ? "" : rest.slice(cut);
  const inner = tail === "" ? "/" : tail.startsWith("/") ? tail : `/${tail}`;
  return {
    instanceId: isInstanceIdShape(segment) ? segment.toLowerCase() : segment,
    inner,
    segment,
  };
}

/** The address of a screen (`/runs/<id>`, with any search) inside an instance. */
export function instancePath(instanceId: string, inner: string): string {
  const base = `${INSTANCE_PATH_PREFIX}/${encodeURIComponent(instanceId)}`;
  if (inner === "" || inner === "/") return base;
  return inner.startsWith("/") ? `${base}${inner}` : `${base}/${inner}`;
}

/**
 * Where a relative screen address goes in an instance: under `/i/<id>/` unless the screen lives
 * outside every instance (platform, personal, the picker), which keeps its own address.
 */
export function addressIn(instanceId: string, address: string): string {
  // An address already in an instance names the screen inside it; this one is moved.
  const { inner } = splitInstancePath(address);
  const pathname = inner.split(/[?#]/)[0] ?? inner;
  return isGlobalPath(pathname) ? inner : instancePath(instanceId, inner);
}

/** The search string without its `instance` parameter (the shim for links sent before T-58). */
export function searchWithoutInstance(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("instance");
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
